import { useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import type { SalarySlip } from '../../types';

interface UsePayslipPdfArgs {
  slip: SalarySlip | null;
}

/**
 * Wraps `react-to-print` for the payslip document.
 *
 * Usage:
 *   const { ref, download } = usePayslipPdf({ slip });
 *   return (<><button onClick={download}>Download</button><PayslipDocument ref={ref} ... /></>);
 *
 * Filename pattern: `Payslip-{employeeCode||employeeId-prefix}-{YYYY-MM}.pdf`,
 * applied via document.title swap (react-to-print picks up document.title for
 * the save-as dialog name).
 */
export function usePayslipPdf({ slip }: UsePayslipPdfArgs) {
  const ref = useRef<HTMLDivElement | null>(null);

  const filename = (() => {
    if (!slip) return 'Payslip';
    const code = slip.employeeSnapshot?.employeeCode || slip.employeeId.slice(0, 8);
    return `Payslip-${code}-${slip.month}`;
  })();

  const print = useReactToPrint({
    contentRef: ref,
    documentTitle: filename,
    pageStyle: `
      @page { size: A4; margin: 12mm; }
      @media print {
        body { background: #FFFFFF !important; }
        .payslip-toolbar, .MuiAppBar-root, .no-print { display: none !important; }
      }
    `,
  });

  const download = useCallback(() => {
    if (!ref.current) return;
    print();
  }, [print]);

  return { ref, download, filename };
}
